import React from 'react';

/**
 * A labelled control, with its hint and its error wired up properly.
 *
 * The reason this exists rather than each screen nesting its own markup:
 * anything inside a `<label>` becomes part of the control's accessible
 * name. A character counter and a paragraph of guidance inside the label
 * turn "Impact" into "Impact Correct it if triage disagrees with how it
 * was rated…", which is what a screen reader then announces on focus.
 *
 * So the label holds the label. The hint and the error sit outside it and
 * reach the control through aria-describedby, and the counter stays inside
 * for layout but is hidden from assistive technology.
 */
export default function Field({ id, label, counter, hint, error, children }) {
  const hintId = hint ? `${id}-hint` : null;
  const errorId = error ? `${id}-error` : null;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })
    : children;

  return (
    <div className="iv-field">
      <label className="iv-label" htmlFor={id}>
        {label}
        {counter ? <span aria-hidden="true">{counter}</span> : null}
      </label>
      {control}
      {error ? (
        <span className="iv-hint iv-hint--error" id={errorId}>
          {error}
        </span>
      ) : null}
      {!error && hint ? (
        <span className="iv-hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
